import React from "react";
import Dialog from "@imminently/decisively-ui/Dialog";
import SelectField from "@imminently/decisively-ui/SelectField";
import Form, { FieldWatch } from "@imminently/decisively-ui/Form";
import FormPanel from "@imminently/decisively-ui/FormPanel";

import { useAuth } from "@/legacy/redux";
import { storeScope } from "@/redux/scope";
import { clearTenancySelectionRequest } from "./recovery";
import { getActiveTenancy, setActiveTenancy } from "./storage";

export interface TenancyDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  blocking?: boolean;
  forbidden?: boolean;
}

type TenancyFormValues = {
  tenancy: string;
};

export const TenancyDialog = ({
  open,
  onOpenChange,
  blocking = false,
  forbidden = false,
}: TenancyDialogProps) => {
  const { user } = useAuth();
  const [initialTenant, setInitialTenant] = React.useState(() => getActiveTenancy());
  const [storageError, setStorageError] = React.useState<string | null>(null);

  const tenancies: Array<{ id: string; name?: string }> = user?.groups ?? [];
  const tenancyOptions = tenancies.map((tenancy) => ({
    label: tenancy.name || tenancy.id,
    value: tenancy.id,
  }));

  React.useEffect(() => {
    if (open) {
      setInitialTenant(forbidden ? null : getActiveTenancy());
      setStorageError(null);
    }
  }, [open, forbidden]);

  const displayedInitialTenant = forbidden ? null : initialTenant;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!blocking || nextOpen) {
      onOpenChange?.(nextOpen);
    }
  };

  return (
    <Dialog
      intent="compact"
      open={open}
      onOpenChange={handleOpenChange}
    >
      <Dialog.Content asChild={true}>
        <Form<TenancyFormValues>
          key={displayedInitialTenant ?? "none"}
          defaultValues={{ tenancy: displayedInitialTenant ?? "" }}
        >
          {(form) => (
            <>
              <Dialog.Header>
                <Dialog.Title>{blocking ? "Select a tenancy to continue" : "Tenancy"}</Dialog.Title>
                <Dialog.Description>
                  {forbidden
                    ? "You no longer have access to the selected tenancy. Choose another tenancy to continue."
                    : "Select the tenancy for this tab."}
                </Dialog.Description>
                {!blocking && <Dialog.Controls />}
              </Dialog.Header>
              <Dialog.Body>
                <FormPanel>
                  <SelectField
                    name="tenancy"
                    label="Tenancy"
                    options={tenancyOptions}
                    fullWidth
                  />
                  {storageError && <p className="text-sm text-destructive">{storageError}</p>}
                  {tenancyOptions.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No tenancies are available for this account. Contact an administrator for access.
                    </p>
                  )}
                </FormPanel>
              </Dialog.Body>
              <Dialog.Actions>
                <FieldWatch names={["tenancy"]}>
                  {({ tenancy }) => (
                    <Dialog.Actions.PrimaryButton
                      onClick={() =>
                        form.handleSubmit((values) => {
                          if (!setActiveTenancy(values.tenancy)) {
                            setStorageError(
                              "The tenancy could not be saved in this tab. Check browser storage settings and try again.",
                            );
                            return;
                          }

                          clearTenancySelectionRequest();
                          storeScope({});
                          window.location.reload();
                        })()
                      }
                      disabled={!tenancy}
                      label={blocking ? "Continue" : "Change"}
                    />
                  )}
                </FieldWatch>
              </Dialog.Actions>
            </>
          )}
        </Form>
      </Dialog.Content>
    </Dialog>
  );
};
